import React, { useEffect, useState, createElement } from 'react';
import { ArrowRightIcon, TrendingUpIcon, SearchIcon, StarIcon, MonitorIcon, CodeIcon, ServerIcon, SearchCheckIcon, UserIcon, BuildingIcon, MapPinIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { FinalCtaSection } from './FinalCtaSection';
interface NearbyLocation {
  name: string;
  slug: string;
}
interface FaqItem {
  q: string;
  a: string;
}
interface LocalPageProps {
  cityName: string;
  region: string;
  introText: string;
  localContext: string;
  nearbyLocations: NearbyLocation[];
  faqs: FaqItem[];
}
export function LocalPageTemplate({
  cityName,
  region,
  introText,
  localContext,
  nearbyLocations,
  faqs
}: LocalPageProps) {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = `Webdesign ${cityName} | Professioneel webdesign | Noord`;
    // Meta description
    const desc = `Professioneel webdesign in ${cityName}. ${introText.length > 120 ? introText.substring(0, 120) + '...' : introText}`;
    let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement;
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = desc;
    // Open Graph meta tags
    const ogTags: Record<string, string> = {
      'og:title': `Webdesign ${cityName} | Professioneel webdesign | Noord`,
      'og:description': desc,
      'og:type': 'website',
      'og:url': `https://noorddigital.nl/webdesign-${cityName.toLowerCase().replace(/\s+/g, '-')}`,
      'og:site_name': 'Noord Digital',
      'og:locale': 'nl_NL'
    };
    Object.entries(ogTags).forEach(([property, content]) => {
      let tag = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.content = content;
    });
    // JSON-LD: LocalBusiness schema
    const localBusinessSchema = {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      name: 'Noord Digital',
      description: `Professioneel webdesign en SEO voor ondernemers in ${cityName}. Conversiegerichte websites die resultaat opleveren.`,
      url: `https://noorddigital.nl/webdesign-${cityName.toLowerCase().replace(/\s+/g, '-')}`,
      telephone: '+31616265214',
      email: 'info@noorddigital.nl',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Schuit 8a',
        addressLocality: 'Oudkarspel',
        postalCode: '1724 BD',
        addressCountry: 'NL'
      },
      areaServed: {
        '@type': 'City',
        name: cityName
      },
      priceRange: '€€',
      sameAs: ['https://facebook.com', 'https://instagram.com', 'https://linkedin.com']
    };
    // JSON-LD: FAQPage schema
    const faqSchema = faqs.length > 0 ? {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map(faq => ({
        '@type': 'Question',
        name: faq.q,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.a
        }
      }))
    } : null;
    const script1 = document.createElement('script');
    script1.type = 'application/ld+json';
    script1.text = JSON.stringify(localBusinessSchema);
    script1.id = 'ld-local-business';
    document.head.appendChild(script1);
    let script2: HTMLScriptElement | null = null;
    if (faqSchema) {
      script2 = document.createElement('script');
      script2.type = 'application/ld+json';
      script2.text = JSON.stringify(faqSchema);
      script2.id = 'ld-faq';
      document.head.appendChild(script2);
    }
    return () => {
      const el1 = document.getElementById('ld-local-business');
      if (el1) el1.remove();
      const el2 = document.getElementById('ld-faq');
      if (el2) el2.remove();
    };
  }, [cityName, faqs]);
  const scrollToContact = (e: React.MouseEvent) => {
    e.preventDefault();
    document.getElementById('contact')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };
  return <main className="pt-16 lg:pt-[72px]" data-id="element-736">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 bg-navy-900 overflow-hidden" data-id="element-737">
        <div className="absolute inset-0 pointer-events-none" data-id="element-738">
          <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full opacity-20 blur-3xl bg-accent" data-id="element-739" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full opacity-10 blur-3xl bg-accent-light" data-id="element-740" />
          <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '32px 32px'
        }} data-id="element-741" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-742">
          <p className="text-accent font-semibold text-sm tracking-widest uppercase mb-4 animate-fade-in-up" data-id="element-743">
            {region}
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6 animate-fade-in-up" data-id="element-744">
            Webdesign {cityName}
          </h1>
          <p className="text-lg lg:text-xl text-navy-200 max-w-2xl mx-auto mb-10 animate-fade-in-up-delay-1" data-id="element-745">
            {introText}
          </p>
          <div className="animate-fade-in-up-delay-2" data-id="element-746">
            <a href="#contact" onClick={scrollToContact} className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-bold px-8 py-4 rounded-button shadow-medium hover:shadow-card transition-all duration-200 text-lg" data-id="element-747">
              Plan een gesprek
              <ArrowRightIcon className="w-5 h-5" data-id="element-748" />
            </a>
          </div>
        </div>
      </section>

      {/* Lokale context */}
      <section className="py-24 bg-surface-muted" data-id="element-749">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-750">
          <div className="text-center mb-16" data-id="element-751">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-752">
              Waarom een goede website in {cityName} onmisbaar is
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto" data-id="element-753">
              {localContext}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-754">
            <div className="bg-white p-8 rounded-card shadow-soft" data-id="element-755">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-756">
                <TrendingUpIcon className="w-6 h-6 text-accent" data-id="element-757" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-758">
                Meer klanten uit {cityName}
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-759">
                Zet websitebezoekers om in betalende klanten met een
                conversiegerichte website die aansluit bij de lokale markt.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-soft" data-id="element-760">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-761">
                <SearchIcon className="w-6 h-6 text-accent" data-id="element-762" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-763">
                Lokaal vindbaar in Google
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-764">
                Word gevonden door potentiële klanten uit {cityName} en
                omstreken die zoeken naar jouw diensten.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-soft" data-id="element-765">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-766">
                <StarIcon className="w-6 h-6 text-accent" data-id="element-767" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-768">
                Professionele uitstraling
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-769">
                Maak een sterke eerste indruk met een modern webdesign dat
                vertrouwen wekt bij klanten in de regio.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Onze diensten */}
      <section className="py-24 bg-white" data-id="element-770">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-771">
          <div className="text-center mb-16" data-id="element-772">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-773">
              Onze diensten in {cityName}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-774">
              Alles wat je nodig hebt voor online succes, onder één dak.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-775">
            <a href="#/website-laten-maken" className="group border border-navy-100 rounded-card p-6 hover:shadow-medium hover:border-accent/20 transition-all duration-300" data-id="element-776">
              <div className="w-12 h-12 rounded-xl bg-navy-50 group-hover:bg-accent-light flex items-center justify-center mb-4 transition-colors duration-300" data-id="element-777">
                <MonitorIcon className="w-6 h-6 text-navy-700 group-hover:text-accent transition-colors duration-300" data-id="element-778" />
              </div>
              <h3 className="text-lg font-semibold text-navy-900 mb-2" data-id="element-779">
                Website laten maken
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed mb-3" data-id="element-780">
                Conversiegerichte websites die bezoekers omzetten in klanten.
              </p>
              <span className="inline-flex items-center gap-1.5 text-accent font-semibold text-sm group-hover:gap-2.5 transition-all" data-id="element-781">
                Meer info
                <ArrowRightIcon className="w-4 h-4" data-id="element-782" />
              </span>
            </a>

            <a href="#/seo-voor-websites" className="group border border-navy-100 rounded-card p-6 hover:shadow-medium hover:border-accent/20 transition-all duration-300" data-id="element-783">
              <div className="w-12 h-12 rounded-xl bg-navy-50 group-hover:bg-accent-light flex items-center justify-center mb-4 transition-colors duration-300" data-id="element-784">
                <SearchCheckIcon className="w-6 h-6 text-navy-700 group-hover:text-accent transition-colors duration-300" data-id="element-785" />
              </div>
              <h3 className="text-lg font-semibold text-navy-900 mb-2" data-id="element-786">
                SEO optimalisatie
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed mb-3" data-id="element-787">
                Hoger scoren in Google en meer organisch verkeer genereren.
              </p>
              <span className="inline-flex items-center gap-1.5 text-accent font-semibold text-sm group-hover:gap-2.5 transition-all" data-id="element-788">
                Meer info
                <ArrowRightIcon className="w-4 h-4" data-id="element-789" />
              </span>
            </a>

            <a href="#/wordpress-websites" className="group border border-navy-100 rounded-card p-6 hover:shadow-medium hover:border-accent/20 transition-all duration-300" data-id="element-790">
              <div className="w-12 h-12 rounded-xl bg-navy-50 group-hover:bg-accent-light flex items-center justify-center mb-4 transition-colors duration-300" data-id="element-791">
                <CodeIcon className="w-6 h-6 text-navy-700 group-hover:text-accent transition-colors duration-300" data-id="element-792" />
              </div>
              <h3 className="text-lg font-semibold text-navy-900 mb-2" data-id="element-793">
                WordPress websites
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed mb-3" data-id="element-794">
                Flexibel en makkelijk te beheren met het populairste CMS.
              </p>
              <span className="inline-flex items-center gap-1.5 text-accent font-semibold text-sm group-hover:gap-2.5 transition-all" data-id="element-795">
                Meer info
                <ArrowRightIcon className="w-4 h-4" data-id="element-796" />
              </span>
            </a>

            <a href="#/hosting-onderhoud" className="group border border-navy-100 rounded-card p-6 hover:shadow-medium hover:border-accent/20 transition-all duration-300" data-id="element-797">
              <div className="w-12 h-12 rounded-xl bg-navy-50 group-hover:bg-accent-light flex items-center justify-center mb-4 transition-colors duration-300" data-id="element-798">
                <ServerIcon className="w-6 h-6 text-navy-700 group-hover:text-accent transition-colors duration-300" data-id="element-799" />
              </div>
              <h3 className="text-lg font-semibold text-navy-900 mb-2" data-id="element-800">
                Hosting & onderhoud
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed mb-3" data-id="element-801">
                Snel, veilig en altijd up-to-date. Wij regelen het.
              </p>
              <span className="inline-flex items-center gap-1.5 text-accent font-semibold text-sm group-hover:gap-2.5 transition-all" data-id="element-802">
                Meer info
                <ArrowRightIcon className="w-4 h-4" data-id="element-803" />
              </span>
            </a>
          </div>
        </div>
      </section>

      {/* Proces */}
      <section className="py-24 bg-navy-900 relative overflow-hidden" data-id="element-804">
        <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
        backgroundSize: '40px 40px'
      }} data-id="element-805" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-806">
          <div className="text-center mb-16" data-id="element-807">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4" data-id="element-808">
              In 4 stappen naar jouw website
            </h2>
            <p className="text-lg text-navy-300 max-w-2xl mx-auto" data-id="element-809">
              Van kennismaking tot livegang — wij ontzorgen je volledig.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6" data-id="element-810">
            {[{
            step: 'Intake',
            desc: 'We bespreken jouw wensen, doelgroep en doelen.'
          }, {
            step: 'Ontwerp',
            desc: 'Een op maat gemaakt design dat past bij jouw merk.'
          }, {
            step: 'Bouw',
            desc: 'Technisch perfect gebouwd, snel en SEO-klaar.'
          }, {
            step: 'Live',
            desc: 'Jouw website gaat live met onderhoud en support.'
          }].map((item, i) => <div key={item.step} className="bg-white/[0.06] border border-white/10 p-6 rounded-2xl text-center relative" data-id="element-811">
                <div className="w-10 h-10 rounded-full bg-accent/20 text-accent flex items-center justify-center mx-auto mb-4 font-bold" data-id="element-812">
                  {i + 1}
                </div>
                <h3 className="text-white font-bold mb-2" data-id="element-813">{item.step}</h3>
                <p className="text-navy-300 text-sm" data-id="element-814">{item.desc}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Voor wie */}
      <section className="py-24 bg-surface-muted" data-id="element-815">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-816">
          <div className="text-center mb-16" data-id="element-817">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-818">
              Voor wie wij bouwen in {cityName}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-819">
            <div className="bg-white p-8 rounded-card shadow-soft text-center" data-id="element-820">
              <div className="w-12 h-12 rounded-full bg-accent-light flex items-center justify-center mx-auto mb-4" data-id="element-821">
                <UserIcon className="w-6 h-6 text-accent" data-id="element-822" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-823">ZZP'ers</h3>
              <p className="text-slate-600" data-id="element-824">
                Professionele websites voor zelfstandigen die een sterke online
                start willen maken.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-soft text-center" data-id="element-825">
              <div className="w-12 h-12 rounded-full bg-accent-light flex items-center justify-center mx-auto mb-4" data-id="element-826">
                <BuildingIcon className="w-6 h-6 text-accent" data-id="element-827" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-828">
                MKB bedrijven
              </h3>
              <p className="text-slate-600" data-id="element-829">
                Uitgebreide websites en maatwerk oplossingen voor groeiende
                bedrijven in de regio.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-soft text-center" data-id="element-830">
              <div className="w-12 h-12 rounded-full bg-accent-light flex items-center justify-center mx-auto mb-4" data-id="element-831">
                <MapPinIcon className="w-6 h-6 text-accent" data-id="element-832" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-833">
                Lokale ondernemers
              </h3>
              <p className="text-slate-600" data-id="element-834">
                Lokale vindbaarheid en conversie voor winkels, horeca en
                dienstverleners.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      {faqs.length > 0 && <section className="py-24 bg-white" data-id="element-835">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-836">
            <div className="text-center mb-16" data-id="element-837">
              <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-838">
                Veelgestelde vragen over webdesign in {cityName}
              </h2>
            </div>

            <div className="space-y-4" data-id="element-839">
              {faqs.map((faq, index) => <div key={index} className="bg-surface-muted rounded-xl shadow-sm border border-navy-50 overflow-hidden" data-id="element-840">
                  <button onClick={() => toggleFaq(index)} className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none" data-id="element-841">
                    <span className="font-bold text-navy-900 pr-4" data-id="element-842">
                      {faq.q}
                    </span>
                    {openFaq === index ? <ChevronUpIcon className="w-5 h-5 text-accent flex-shrink-0" data-id="element-843" /> : <ChevronDownIcon className="w-5 h-5 text-navy-400 flex-shrink-0" data-id="element-844" />}
                  </button>
                  {openFaq === index && <div className="px-6 pb-5 text-slate-600 leading-relaxed" data-id="element-845">
                      {faq.a}
                    </div>}
                </div>)}
            </div>
          </div>
        </section>}

      {/* Silo footer — Nearby locations + internal links */}
      <section className="py-16 bg-navy-50 border-y border-navy-100" data-id="element-846">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-847">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10" data-id="element-848">
            {/* Nearby locations */}
            <div data-id="element-849">
              <h3 className="text-sm font-semibold text-navy-900 uppercase tracking-wider mb-4" data-id="element-850">
                Webdesign in de buurt
              </h3>
              <nav className="flex flex-col gap-2" data-id="element-851">
                {nearbyLocations.map(loc => <a key={loc.slug} href={`#/webdesign-${loc.slug}`} className="text-sm text-navy-600 hover:text-accent transition-colors" data-id="element-852">
                    Webdesign {loc.name}
                  </a>)}
              </nav>
            </div>

            {/* Services */}
            <div data-id="element-853">
              <h3 className="text-sm font-semibold text-navy-900 uppercase tracking-wider mb-4" data-id="element-854">
                Onze diensten
              </h3>
              <nav className="flex flex-col gap-2" data-id="element-855">
                <a href="#/website-laten-maken" className="text-sm text-navy-600 hover:text-accent transition-colors" data-id="element-856">
                  Professionele website laten maken
                </a>
                <a href="#/seo-voor-websites" className="text-sm text-navy-600 hover:text-accent transition-colors" data-id="element-857">
                  SEO optimalisatie
                </a>
                <a href="#/wordpress-websites" className="text-sm text-navy-600 hover:text-accent transition-colors" data-id="element-858">
                  WordPress website bouwen
                </a>
                <a href="#/hosting-onderhoud" className="text-sm text-navy-600 hover:text-accent transition-colors" data-id="element-859">
                  Hosting en technisch beheer
                </a>
              </nav>
            </div>

            {/* Kennisbank */}
            <div data-id="element-860">
              <h3 className="text-sm font-semibold text-navy-900 uppercase tracking-wider mb-4" data-id="element-861">
                Kennisbank
              </h3>
              <nav className="flex flex-col gap-2" data-id="element-862">
                <a href="#/kosten-website-laten-maken" className="text-sm text-navy-600 hover:text-accent transition-colors" data-id="element-863">
                  Wat kost een website?
                </a>
                <a href="#/website-laten-maken-checklist" className="text-sm text-navy-600 hover:text-accent transition-colors" data-id="element-864">
                  Website checklist
                </a>
                <a href="#/wordpress-vs-wix" className="text-sm text-navy-600 hover:text-accent transition-colors" data-id="element-865">
                  WordPress of Wix kiezen?
                </a>
                <a href="#/" className="text-sm text-navy-600 hover:text-accent transition-colors" data-id="element-866">
                  Terug naar homepage
                </a>
              </nav>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <FinalCtaSection data-id="element-867" />
    </main>;
}